import { useState, useRef } from 'react'
import { Loader2, Bot, TriangleAlert } from 'lucide-react'
import { useT } from '../../hooks/useTranslation'
import { useHaptic } from '../../hooks/useHaptic'
import { useDashboardStore } from '../../store/dashboardStore'
import { botApi } from '../../services/botApi'

// Онбординг первого входа (п.0.3, 2026-08-31): флоу «Вход → Экран 1 (value prop +
// CTA «Попробовать бесплатно») → Экран 2 («Создаём пробного бота...»,
// автосоздание + автостарт) → Дашборд с пробным ботом (TrialStatusCard)».
// Показывается НОВОМУ юзеру (нет подписки, нет ботов, не админ); в «режиме
// юзера» (имперсонация) тестовый юзер сбрасывается — флоу видно при каждом входе.
//
// Решение «по-другому» относительно task.txt: Экран 1 НЕ переходит на Экран 2
// сам по истечении 3 секунд — создавать бота без явного согласия мы не хотим.
// CTA «Попробовать бесплатно» доступна сразу (value-пропозиция читается за
// первые секунды), тайм-тут: Экран 2 держим ~4с минимум (ощущение «создаём»),
// TTFV всё равно < 30 секунд.
const MIN_CREATE_MS = 4000

export default function OnboardingFlow({ onDone, onSkip }) {
  const t = useT()
  const haptic = useHaptic()
  const setUserBots = useDashboardStore(s => s.setUserBots)
  const [step, setStep] = useState('hero')     // 'hero' | 'creating' | 'error'
  const [error, setError] = useState(null)
  const startedRef = useRef(false)

  const createTrialBot = async () => {
    if (startedRef.current) return
    startedRef.current = true
    setStep('creating')
    setError(null)
    haptic.medium()
    try {
      const [res] = await Promise.all([
        botApi.createVirtualBot(),
        new Promise(r => setTimeout(r, MIN_CREATE_MS)),
      ])
      const bot = res?.bot
      if (!bot) throw new Error('empty server response')
      setUserBots(prev =>
        prev.some(b => String(b.id) === String(bot.id))
          ? prev
          : [...prev, { ...bot, accountMode: bot.accountMode || 'demo' }]
      )
      // Пробуем запустить автоматически. Если не выйдет — на Дашборде
      // TrialStatusCard покажет CTA «Запустить» (п.0.3 task.txt).
      try {
        await botApi.startBot(bot.id)
        setUserBots(prev => prev.map(b => String(b.id) === String(bot.id) ? { ...b, status: 'active' } : b))
      } catch { /* не критично: есть CTA «Запустить» */ }
      onDone?.()
    } catch (err) {
      startedRef.current = false
      setStep('error')
      setError(err?.message || String(err))
    }
  }

  const skip = () => { haptic.light(); onSkip?.() }

  const btnPrimary = {
    width: '100%', padding: '15px', borderRadius: 14,
    fontSize: 16, fontWeight: 700,
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: '#fff', border: 'none', cursor: 'pointer',
    boxShadow: '0 6px 24px rgba(16,185,129,0.35)',
    WebkitTapHighlightColor: 'transparent',
  }
  const btnGhost = {
    width: '100%', padding: '13px', borderRadius: 14,
    fontSize: 14, fontWeight: 600,
    background: 'rgba(255,255,255,0.05)',
    color: '#8E8E93', border: '1px solid rgba(255,255,255,0.08)',
    cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 13000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '28px 28px 40px',
        background: 'radial-gradient(120% 90% at 50% 0%, #0D3B2E 0%, #0A0F0C 55%, #050505 100%)',
      }}
    >
      {/* Мягкое emerald-свечение сверху (как у приложения) */}
      <div aria-hidden="true" style={{
        position: 'absolute', top: 0, right: 0, width: '70%', height: '46%',
        background: 'radial-gradient(ellipse at 100% 0%, rgba(16,185,129,0.30) 0%, rgba(16,185,129,0.10) 30%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: 380 }}>
        {step === 'hero' && (
          <div style={{ textAlign: 'center', animation: 'scalpexLiveIn 0.5s ease' }}>
            <div style={{
              width: 84, height: 84, borderRadius: 24, margin: '0 auto 22px',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 12px 40px rgba(16,185,129,0.35)',
            }}>
              <Bot size={42} color="#fff" />
            </div>
            <h1 style={{ fontSize: 21, fontWeight: 700, color: '#fff', margin: '0 0 12px', lineHeight: 1.3 }}>
              {t.onbTitle}
            </h1>
            <p style={{ fontSize: 14, color: '#A8A8AD', margin: '0 0 30px', lineHeight: 1.6 }}>
              {t.onbSubtitle}
            </p>

            <button onClick={createTrialBot} style={btnPrimary}>
              {t.onbCta}
            </button>
            <button onClick={skip} style={{ ...btnGhost, marginTop: 10 }}>
              {t.onbLater}
            </button>
          </div>
        )}

        {step === 'creating' && (
          <div style={{ textAlign: 'center', padding: '20px 0', animation: 'scalpexLiveIn 0.5s ease' }}>
            <div style={{
              width: 84, height: 84, borderRadius: '50%', margin: '0 auto 22px',
              background: 'rgba(16,185,129,0.12)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(16,185,129,0.3)',
            }}>
              <Loader2 size={38} color="#34D399" style={{ animation: 'spin 1s linear infinite' }} />
            </div>
            <h1 style={{ fontSize: 19, fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>
              {t.onbCreating}
            </h1>
            <p style={{ fontSize: 13.5, color: '#8E8E93', margin: 0, lineHeight: 1.5 }}>
              {t.onbCreatingHint}
            </p>
          </div>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center', animation: 'scalpexLiveIn 0.5s ease' }}>
            <div style={{
              width: 84, height: 84, borderRadius: 24, margin: '0 auto 22px',
              background: 'rgba(255,59,48,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,59,48,0.3)',
            }}>
              <TriangleAlert size={38} color="#FF6B6B" />
            </div>
            <h1 style={{ fontSize: 19, fontWeight: 700, color: '#fff', margin: '0 0 10px' }}>
              {t.onbCreateFailed}
            </h1>
            <p style={{ fontSize: 12, color: '#636366', margin: '0 0 24px', lineHeight: 1.5, wordBreak: 'break-word' }}>
              {error}
            </p>
            <button onClick={createTrialBot} style={btnPrimary}>{t.onbRetry}</button>
            <button onClick={skip} style={{ ...btnGhost, marginTop: 10 }}>{t.onbLater}</button>
          </div>
        )}
      </div>
    </div>
  )
}
